import { z } from 'zod'
import posthog from 'posthog-js'
import { trackEvent } from '../services/activeCampaign'
import { coreApi } from '../services/api'
import forIn from 'lodash/forIn'
import moment from 'moment'
import {
  DashboardAccessEventSchema,
  DashboardSelectCourseSchema,
  DashboardStudyGoalChangeSchema,
  DownloadContentSchema,
  IdentifyUserEventSchema,
  MessageListPageAccessSchema,
  CourseSchema,
  AccessContentSchema,
  verifySchema,
  QuestionFilterSchema,
  QuestionAnswerSchema,
  ContentSchema,
  BannerSchema,
  TourEventSchema,
} from './schemas'

let PosthogApiKey: string

export interface IFreemiumAccessModule {
  email: string
  name: string
  telephone: string
  career: string
  publicTenderName: string
  courseName: string
  courseId: string
  moduleName: string
}

const FreemiumAccessModule = async (properties: IFreemiumAccessModule) => {
  try {
    // Envio para o AC
    await trackEvent({
      event: 'acessou-o-freemium',
      email: properties.email,
      eventdata: `Edital:${properties.publicTenderName} - Carreira:${properties.career} - Curso:${properties.courseName} - Módulo:${properties.moduleName}`,
    })

    const splitName = {
      firstName: properties.name.split(' ').slice(0, 1).join(),
      lastName: properties.name.split(' ').slice(1).join(' '),
    }

    // Alterando as infos no AC
    coreApi.post('integrations/active-campaign/contacts', {
      email: properties.email,
      phone: properties.telephone,
      firstName: splitName.firstName,
      lastName: splitName.lastName,
      customFields: [
        {
          field: 'freemium_last_career',
          value: properties.career,
        },
        {
          field: 'freemium_last_publicTender',
          value: properties.publicTenderName,
        },
        {
          field: 'freemium_last_course_id',
          value: properties.courseId,
        },
        {
          field: 'freemium_last_course_name',
          value: properties.courseName,
        },
      ],
    })
  } catch (error) {
    console.error(error)
  }
  return
}

const DashboardAccess = async (properties: z.infer<typeof DashboardAccessEventSchema>) => {
  const event = 'Acessar menu desempenho'

  const isValid = verifySchema(DashboardAccessEventSchema, properties, event)

  if (isValid) {
    posthog.capture(event, properties)
  }
}

const DashboardSelectCourse = async (properties: z.infer<typeof DashboardSelectCourseSchema>) => {
  const event = 'Selecionar curso_Menu Desempenho'

  const isValid = verifySchema(DashboardSelectCourseSchema, properties, event)

  if (isValid) {
    posthog.capture(event, properties)
  }
}

const DashboardStudyGoalChange = async (
  properties: z.infer<typeof DashboardStudyGoalChangeSchema>
) => {
  const event = 'Definir meta_Menu Desempenho'

  const isValid = verifySchema(DashboardStudyGoalChangeSchema, properties, event)

  if (isValid) {
    posthog.capture(event, properties)
  }
}

const MessagePageAccess = async (properties: z.infer<typeof MessageListPageAccessSchema>) => {
  const event = 'Ler Recado'

  const isValid = verifySchema(MessageListPageAccessSchema, properties, event)

  if (isValid) {
    posthog.capture(event, properties)
  }
}

const GetDateProperties = ({ label }) => {
  const week_days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
  const now = moment()

  const Dia_Da_Semana = week_days[+now.format('d')],
    Data = now.format(),
    Dia = +now.format('DD'),
    Mes = +now.format('MM'),
    Ano = +now.format('YYYY'),
    Hora = +now.format('HH'),
    Trimestre = `Q${now.quarter()}`,
    Semestre = `S${Math.ceil(now.quarter() / 2)}`

  const properties = { Dia_Da_Semana, Data, Dia, Mes, Ano, Hora, Trimestre, Semestre }

  const dates = {
    first: {},
    last: {},
  }

  forIn(properties, (value, key) => {
    dates.first[`Primeiro_${label}_${key}`] = value
    dates.last[`Ultimo_${label}_${key}`] = value
  })

  return dates
}

const SignIn = async (properties: z.infer<typeof IdentifyUserEventSchema>) => {
  const event = 'Login'
  const isValid = verifySchema(IdentifyUserEventSchema, properties, event)

  if (isValid) {
    const dates = GetDateProperties({ label: 'Login' })

    if (PosthogApiKey) {
      posthog.identify(properties.Id, {
        Email: properties.Email,
        Nome: properties.Nome,
      })

      posthog.capture(event, {
        $set: {
          ...dates.last,
          Email: properties.Email,
          Nome: properties.Nome,
        },
        $set_once: { ...dates.first },
      })
    }
  }
}

const UpdateIdentifyUser = (properties: z.infer<typeof IdentifyUserEventSchema>) => {
  const isValid = verifySchema(IdentifyUserEventSchema, properties)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.identify(properties.Id, {
        Id: properties.Id,
        Email: properties.Email,
        Nome: properties.Nome,
        Plano: properties.Plano,
        Produto: properties.Produto,
        Produtos: properties.Produtos,
      })
    }
  }
}

const DownloadContent = async (properties: z.infer<typeof DownloadContentSchema>) => {
  const event = 'Baixar conteúdo'
  const isValid = verifySchema(DownloadContentSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) posthog.capture(event, properties)
  }
}

const CourseImpression = async (properties: z.infer<typeof CourseSchema>) => {
  const event = 'Visualizar módulos do curso'
  const dates = GetDateProperties({ label: 'Curso' })
  const isValid = verifySchema(CourseSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, {
        ...properties,
        $set: {
          Ultimo_Curso: properties.Curso,
          Ultima_Instituicao: properties.Instituicao,
          Ultima_Carreira: properties.Carreira,
          ...dates.last,
        },
        $set_once: {
          Primeiro_Curso: properties.Curso,
          Primeira_Instituicao: properties.Instituicao,
          Primeira_Carreira: properties.Carreira,
          ...dates.first,
        },
      })
    }
  }
}

const AccessContent = async (properties: z.infer<typeof AccessContentSchema>) => {
  const event = 'Acessar conteúdo'
  const dates = GetDateProperties({ label: 'Curso' })
  const isValid = verifySchema(AccessContentSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, {
        ...properties,
        $set: {
          Ultimo_Curso: properties.Curso,
          Ultima_Instituicao: properties.Instituicao,
          Ultima_Carreira: properties.Carreira,
          ...dates.last,
        },
        $set_once: {
          Primeiro_Curso: properties.Curso,
          Primeira_Instituicao: properties.Instituicao,
          Primeira_Carreira: properties.Carreira,
          ...dates.first,
        },
      })
    }
  }
}

const ContentImpression = async (properties: z.infer<typeof ContentSchema>) => {
  const event = 'Visualizar conteúdo'
  const dates = GetDateProperties({ label: 'Curso' })
  const isValid = verifySchema(ContentSchema, properties, event)

  // if (isValid) {
  //   if (PosthogApiKey) {
  //     posthog.capture(event, {
  //       ...properties,
  //       $set: {
  //         Ultimo_Curso: properties.Curso,
  //         Ultima_Instituicao: properties.Instituicao,
  //         Ultima_Carreira: properties.Carreira,
  //         ...dates.last,
  //       },
  //       $set_once: {
  //         Primeiro_Curso: properties.Curso,
  //         Primeira_Instituicao: properties.Instituicao,
  //         Primeira_Carreira: properties.Carreira,
  //         ...dates.first,
  //       },
  //     })
  //   }
  // }
}

const FormatQuestionFilters = (filters: any) => {
  const properties = {}

  const translate_keys = {
    subjects: 'Disciplina',
    themes: 'Assunto',
    topics: 'Topico',
    years: 'Ano',
    evaluatingInstitutions: 'Banca',
    schooling: 'Escolaridade',
    publicTenders: 'Orgao',
    roles: 'Cargo',
    questionType: 'Tipo_Questao',
    graduation: 'Formacao',
    search: 'Texto',
    myQuestions: 'Minhas_Questoes',
    advanced: 'Avancado',
    includes: 'Anuladas',
  }

  properties['Filtros'] = 0
  properties['Disciplinas'] = 0
  properties['Assuntos'] = 0
  properties['Topicos'] = 0
  properties['Anos'] = 0
  properties['Bancas'] = 0
  properties['Escolaridades'] = 0
  properties['Orgaos'] = 0
  properties['Cargos'] = 0
  properties['Tipos_Questoes'] = 0
  properties['Formacoes'] = 0

  forIn(filters, (value, key) => {
    const label = translate_keys[key]

    if (
      [
        'subjects',
        'themes',
        'topics',
        'years',
        'evaluatingInstitutions',
        'schooling',
        'publicTenders',
        'roles',
        'questionType',
        'graduation',
      ].includes(key)
    ) {
      if (value.length)
        value.map((v, index) => {
          if (v?.label) properties[`${label}_${index + 1}`] = v?.label
        })

      const counter = value.filter((v) => v.label)?.length
      properties[
        key === 'graduation' ? 'Formacoes' : key === 'questionType' ? 'Tipos_Questoes' : `${label}s`
      ] = counter
      properties['Filtros'] += counter
    }

    if (key === 'search' && value.length) {
      properties['Texto'] = value
    }

    if (key === 'myQuestions' && value.length) {
      const translate = { correct: 'Corretas', wrong: 'Erradas', unanswered: 'Não respondidas' }
      properties[label] = value.map((t) => translate[t]).join(' ou ')
      properties['Filtros']++
    }

    if (key === 'advanced') {
      properties[label] = value
    }

    if (key === 'includes') {
      properties[label] = !value.length
      if (properties[label]) properties['Filtros']++
    }
  })

  return properties
}

const ApplyQuestionFilter = async (filters: any) => {
  const event = 'Pesquisar questões'
  const properties = FormatQuestionFilters(filters)

  const isValid = verifySchema(QuestionFilterSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, properties)
    }
  }
}

const CreateQuestionBook = async (filters: any) => {
  const event = 'Criar caderno de questões'
  let properties = FormatQuestionFilters(filters)

  properties = {
    ...properties,
    Questoes_Encontradas: filters.Questoes_Encontradas,
    Questoes_Caderno: filters.Questoes_Caderno,
    Status: filters.Status,
    Caderno_Nome: filters.Caderno_Nome,
  }

  if (filters.Caderno_Id) properties['Caderno_Id'] = filters.Caderno_Id

  const isValid = verifySchema(QuestionFilterSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, properties)
    }
  }
}

const QuestionAnswer = async (question: any) => {
  const event = 'Responder questão'
  const properties = {}

  if (question.type === 'CERTO_ERRADO') {
    properties['Tipo_Questao'] = 'Certo ou errado'
  } else if (question.type === 'MULTIPLA_ESCOLHA' && question.alternatives.length === 4) {
    properties['Tipo_Questao'] = 'Múltipla escolha - 4 alternativas'
  } else if (question.type === 'MULTIPLA_ESCOLHA' && question.alternatives.length === 5) {
    properties['Tipo_Questao'] = 'Múltipla escolha - 5 alternativas'
  }

  properties['Questao'] = question.code
  properties['Ano'] = question?.exam?.year
  properties['Banca'] = question?.exam?.evaluatingInstitution?.abbreviation
  properties['Orgao'] = question?.exam?.publicTender?.abbreviation
  properties['Escolaridade'] = question?.exam?.schooling
  properties['Origem'] = question.source
  properties['Resposta'] = question.selected
  properties['Acertou'] = question.hit

  if (question?.exam?.roles?.length)
    question?.exam?.roles
      .filter((t) => t)
      .map((r, index) => {
        properties[`Cargo_${index + 1}`] = r
      })

  if (question?.exam?.graduation?.length)
    question?.exam?.graduation
      .filter((t) => t)
      .map((g, index) => {
        properties[`Formacao_${index + 1}`] = g
      })

  if (question?.hierarchies?.length) {
    question?.hierarchies
      .filter((t) => t.level == 0)
      .map((h, index) => {
        properties[`Disciplina_${index + 1}`] = h.name
      })
    question?.hierarchies
      .filter((t) => t.level == 1)
      .map((h, index) => {
        properties[`Assunto_${index + 1}`] = h.name
      })
    question?.hierarchies
      .filter((t) => t.level == 2)
      .map((h, index) => {
        properties[`Topico_${index + 1}`] = h.name
      })
  }

  const isValid = verifySchema(QuestionAnswerSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, properties)
    }
  }
}

const TourEvent = async (properties: z.infer<typeof TourEventSchema>) => {
  const event = 'Visualizar tour guiado'
  const isValid = verifySchema(TourEventSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, properties)
    }
  }
}

const BannerImpression = async (properties: z.infer<typeof BannerSchema>) => {
  const event = 'Visualizar banner'
  const isValid = verifySchema(BannerSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, properties)
    }
  }
}

const BannerClick = async (properties: z.infer<typeof BannerSchema>) => {
  const event = 'Acessar oferta do banner'
  const isValid = verifySchema(BannerSchema, properties, event)

  if (isValid) {
    if (PosthogApiKey) {
      posthog.capture(event, properties)
    }
  }
}

const initPosthog = (PosthogApiKeyParam: string | undefined) => {
  try {
    if (PosthogApiKeyParam) {
      PosthogApiKey = PosthogApiKeyParam
      posthog.init(PosthogApiKeyParam, {
        api_host: 'https://app.posthog.com',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
      })
    }
  } catch (error) {
    console.log(error)
  }
}

const app = 'AA'

const dispatch = (data) => {
  const { event, properties } = data

  // if (PosthogApiKey === 'phc_o46E6eXY2ooHjCEWSqzdlnYNnSg3EnfMxoSwlM9NJip')
  //   console.info(event.name, properties)

  delete data.event

  event.call(null, properties)
}

export const TrackingSchemaTypes = {
  FreemiumAccessModule,
  DashboardAccess,
  DashboardSelectCourse,
  DashboardStudyGoalChange,
  MessagePageAccess,
  SignIn,
  DownloadContent,
  AccessContent,
  UpdateIdentifyUser,
  CourseImpression,
  ApplyQuestionFilter,
  CreateQuestionBook,
  QuestionAnswer,
  ContentImpression,
  BannerImpression,
  BannerClick,
  TourEvent,
}

export default { app, dispatch, TrackingSchemaTypes, initPosthog }
